import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw, Download, Eye, X } from "lucide-react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import * as XLSX from "xlsx";
import { useCurrentDomain } from "../../context/DomainContext";
import { useCompanies } from "../../hooks";
import { getBillSearchByNumbers } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import { THEME } from "../../theme/themeColors";
import BillSearchByNumbersReportdetails from "./BillSearchByNumbersReportdetails";
import "./BillSearchByNumbersReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
  { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
  { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear() - 1, 11, 31)] },
];

const CompanyOption = (props) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        cursor: "pointer",
        background: isSelected ? THEME.primary : props.isFocused ? "#f3f4f6" : "#fff",
        color: isSelected ? THEME.onPrimary : "#111827",
      }}
    >
      <input type="checkbox" checked={isSelected} readOnly style={{ margin: 0 }} />
      <span style={{ fontSize: 13 }}>{data.label}</span>
    </div>
  );
};

const formatMoney = (v) =>
  v == null || v === ""
    ? ""
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US");
};

const joinAddress = (address, city, state, zip) =>
  [address, city, state, zip]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const NoteCell = ({ text, onOpen }) => {
  const value = text == null ? "" : String(text);
  if (!value) return <td />;
  const isLong = value.length > 40;
  return (
    <td>
      <div className="bsbn-note-cell">
        <span className="bsbn-note-clamp">{isLong ? value.slice(0, 40) + "..." : value}</span>
        {isLong && (
          <button type="button" className="bsbn-eye" onClick={() => onOpen({ title: "Note", text: value })}>
            <Eye size={14} />
          </button>
        )}
      </div>
    </td>
  );
};

const BillSearchByNumbersReport = () => {
  const { domain } = useCurrentDomain();
  const companies = useCompanies(domain);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [billNumbers, setBillNumbers] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [detailsCtx, setDetailsCtx] = useState(null);
  const [popup, setPopup] = useState(null);

  const [viewMode, setViewMode] = useState("summary");
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const [detailSearch, setDetailSearch] = useState("");
  const [appliedDetailSearch, setAppliedDetailSearch] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [grandTotals, setGrandTotals] = useState({ totalBilled: 0, totalBalance: 0, totalWriteOff: 0, totalPaid: 0 });
  const [totalsLoading, setTotalsLoading] = useState(true);

  const companyIds = useMemo(
    () => (selectedCompanies || []).map((c) => c.value).filter(Boolean).join(","),
    [selectedCompanies]
  );

  const companyList = useMemo(() => {
    if (Array.isArray(companies)) return companies;
    if (Array.isArray(companies?.data)) return companies.data;
    if (Array.isArray(companies?.companies)) return companies.companies;
    return [];
  }, [companies]);

  const sortedOptions = useMemo(() => {
    const selected = new Set((selectedCompanies || []).map((c) => c.value));
    return [...companyList].sort((a, b) => {
      const aSel = selected.has(a.value) ? 0 : 1;
      const bSel = selected.has(b.value) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return String(a.label || "").localeCompare(String(b.label || ""));
    });
  }, [companyList, selectedCompanies]);

  const fromDate = dateRange?.[0] ? dateRange[0].toISOString().slice(0, 10) : null;
  const toDate = dateRange?.[1] ? dateRange[1].toISOString().slice(0, 10) : null;

  const loadGrandTotals = async () => {
    if (!domain) {
      setTotalsLoading(false);
      return;
    }
    setTotalsLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        tabName: "Totals",
      });
      const data = res?.totals ? res : res?.data || {};
      const t = data?.totals || {};
      setGrandTotals({
        totalBilled: t.totalBilled || 0,
        totalBalance: t.totalBalance || 0,
        totalWriteOff: t.totalWriteOff || 0,
        totalPaid: t.totalPaid || 0,
      });
    } catch {
    } finally {
      setTotalsLoading(false);
    }
  };

  useEffect(() => {
    loadGrandTotals();
  }, [domain]);

  const loadSummary = async () => {
    if (!domain) return toast.error("Domain is missing");
    if (!billNumbers.trim() && !companyIds) return toast.error("Bill Numbers or Company is required");
    if ((fromDate || toDate) && !companyIds) return toast.error("Select a company to use date range");

    setLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers.trim(),
        companyIds,
        fromDate,
        toDate,
        tabName: "Summary",
        pageNumber: 1,
        pageSize: 25,
        isExport: false,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      setSummary(list);
      if (!list.length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load");
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (companyId, companyName) => {
    if (!companyId) return;
    setDetailsCtx({
      domain,
      companyId,
      companyName: companyName || "",
      billNumbers: billNumbers.trim(),
      fromDate,
      toDate,
    });
  };

  const handleReset = () => {
    setSelectedCompanies([]);
    setBillNumbers("");
    setDateRange(null);
    setSummary([]);
    setViewMode("summary");
    setDetailRows([]);
    setDetailTotal(0);
    setDetailPage(1);
    setDetailSearch("");
    setAppliedDetailSearch("");
  };

  const handleDownload = async () => {
    if (!billNumbers.trim() && !companyIds) return toast.error("Bill Numbers or Company is required");
    setLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers.trim(),
        companyIds,
        fromDate,
        toDate,
        tabName: "Summary",
        isExport: true,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      if (!list.length) return toast.error("No data to download");
      const mapped = list.map((r) => ({
        "Company Name": r.companyName,
        "Company Id": r.companyId,
        Bills: r.totalCount,
        Billed: r.totalBilled,
        Balance: r.totalBalance,
        "Write Off": r.totalWriteOff,
        Paid: r.totalPaid,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
      XLSX.writeFile(wb, "BillSearchByNumbers_Summary.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);

  const loadAllDetails = async (page, size, search = appliedDetailSearch) => {
    if (!domain) return toast.error("Domain is missing");
    if (!billNumbers.trim() && !companyIds) return toast.error("Bill Numbers or Company is required");
    if ((fromDate || toDate) && !companyIds) return toast.error("Select a company to use date range");

    setDetailLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers.trim(),
        companyIds,
        fromDate,
        toDate,
        tabName: "Details",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.details ? res : res?.data || {};
      setDetailRows(data?.details || []);
      setDetailTotal(data?.totalCount || 0);
      setDetailPage(page);
      setDetailPageSize(size);
      setAppliedDetailSearch(search || "");
      if (!(data?.details || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setDetailRows([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailSearch = () => loadAllDetails(1, detailPageSize, detailSearch.trim());

  const mapDetailRow = (r) => ({
    Attorney: r.attorney,
    "Bill Number": r.szBillNumber,
    "Case No": r.szCaseNo,
    "Bill Date": formatDate(r.dtBillDate),
    "Billed Amount": r.fltBillAmount,
    "Paid Amount": r.paidAmount,
    "WriteOff Amount": r.fltWriteOff,
    "Paid Flag": r.bitPaid,
    "Amount Applied": r.fltAmountApplied,
    Balance: r.fltBalance,
    Interest: r.fltInterest,
    Type: r.szType,
    "Test Type": r.szTestType,
    "Write Off Flag": r.bitWriteOffFlag,
    "Payment State": r.iPaymentState,
    Status: r.szBillStatusName,
    "First Visit": formatDate(r.dtFirstVisitDate),
    "Proc Date": r.procDate,
    Specialty: r.speciality,
    "Procedure Group": r.szProcedureGroup,
    "Status Code": r.szBillStatusCode,
    Transfer: r.btTransfer ? "Yes" : "No",
    Denial: r.btDenial,
    Verification: r.btVerification,
    EOR: r.btEor,
    Provider: r.szProviderName,
    "Case Type": r.mstCaseType,
    "Case Status": r.caseStatus,
    Notes: r.szBillNotes,
    Company: r.companyName,
    "First Name": r.szPatientFirstName,
    "Last Name": r.szPatientLastName,
    Patient: r.szPatientName,
    "Claim #": r.szClaimNumber,
    "Policy #": r.szPolicyNo,
    "Has Payment": r.hasPayment,
    Collection: r.collectionName,
    "Collection Id": r.szBillCollectionId,
    Insurance: r.insuranceName,
    "Denial Reasons": r.denialReasons,
    VerificationText: r.verification,
    DOB: formatDate(r.dob),
    SSN: r.ssn,
    Cell: r.patientCellPhone,
    "Home Phone": r.patientHomePhone,
    DOA: formatDate(r.doa),
    Address: joinAddress(r.address, r.city, r.state, r.zip),
    "PI Attorney": r.piAttorney,
    "Attorney Phone": r.attorneyPhone,
    Funder: r.funderName,
    Portfolio: r.portfolioName,
    Batch: r.batchNumber,
    Funded: r.isFunded,
    "Funder Note": r.funderNote,
  });

  const handleDetailDownload = async () => {
    if (!billNumbers.trim() && !companyIds) return toast.error("Bill Numbers or Company is required");
    setDetailLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers.trim(),
        companyIds,
        fromDate,
        toDate,
        tabName: "Details",
        isExport: true,
      });
      const data = res?.details ? res : res?.data || {};
      const list = data?.details || [];
      if (!list.length) return toast.error("No data to download");
      const mapped = list.map(mapDetailRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "BillSearchByNumbers_Details.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setDetailLoading(false);
    }
  };

  if (detailsCtx) {
    return (
      <BillSearchByNumbersReportdetails
        domain={detailsCtx.domain}
        companyId={detailsCtx.companyId}
        companyName={detailsCtx.companyName}
        billNumbers={detailsCtx.billNumbers}
        fromDate={detailsCtx.fromDate}
        toDate={detailsCtx.toDate}
        onBack={() => setDetailsCtx(null)}
      />
    );
  }

  return (
    <div className="bsbn-page">
      <Toaster position="top-right" />

      <div className="bsbn-filters">
        <div className="bsbn-field bsbn-company">
          <label>Company</label>
          <Select
            options={sortedOptions}
            value={selectedCompanies}
            onChange={(opts) => {
              const next = opts || [];
              setSelectedCompanies(next);
              if (!next.length) setDateRange(null);
            }}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 36,
                height: "auto",
                alignSelf: "center",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                gap: 3,
                paddingTop: 3,
                paddingBottom: 3,
                paddingLeft: 6,
                paddingRight: 6,
              }),
              multiValue: (base) => ({
                ...base,
                margin: 0,
                maxWidth: "100%",
                background: THEME.alpha(0.12),
              }),
              multiValueLabel: (base) => ({
                ...base,
                whiteSpace: "normal",
                fontSize: 11,
                lineHeight: "14px",
                padding: "2px 4px",
              }),
              multiValueRemove: (base) => ({
                ...base,
                paddingLeft: 2,
                paddingRight: 2,
              }),
            }}
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ Option: CompanyOption }}
            placeholder="Select company(s)"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="bsbn-field">
          <label>Bill Numbers</label>
          <input
            className="bsbn-input"
            value={billNumbers}
            onChange={(e) => setBillNumbers(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (viewMode === "summary" ? loadSummary() : loadAllDetails(1, detailPageSize))}
            placeholder="Paste bill numbers (comma or new line)"
          />
        </div>

        <div className="bsbn-field bsbn-date">
          <label>Date Range</label>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            ranges={predefinedBottomRanges}
            format="MM/dd/yyyy"
            placeholder="Select dates"
            cleanable
            disabled={!companyIds}
            style={{ width: 210 }}
          />
        </div>

        <div className="bsbn-actions">
          <button
            type="button"
            className="bsbn-btn bsbn-btn-search"
            onClick={() => (viewMode === "summary" ? loadSummary() : loadAllDetails(1, detailPageSize))}
            disabled={loading || detailLoading}
          >
            <Search size={14} />
            {loading || detailLoading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="bsbn-btn bsbn-btn-reset" onClick={handleReset} disabled={loading || detailLoading}>
            <RefreshCw size={14} />
            Reset
          </button>
          {viewMode === "summary" && (
            <button type="button" className="bsbn-btn bsbn-btn-reset" onClick={handleDownload} disabled={loading || !summary.length}>
              <Download size={14} />
              Excel
            </button>
          )}
        </div>
      </div>

      <div className="bsbn-toggle-row">
        <div className="bsbn-toggle">
          <button
            type="button"
            className={`bsbn-toggle-btn ${viewMode === "summary" ? "active" : ""}`}
            onClick={() => {
              setViewMode("summary");
              if (billNumbers.trim() || companyIds) loadSummary();
            }}
          >
            Summary
          </button>
          <button
            type="button"
            className={`bsbn-toggle-btn ${viewMode === "detailed" ? "active" : ""}`}
            onClick={() => {
              setViewMode("detailed");
              if (billNumbers.trim() || companyIds) loadAllDetails(1, detailPageSize);
            }}
          >
            Detailed
          </button>
        </div>

        <div className="bsbn-grand-totals">
          {totalsLoading ? (
            <span className="bsbn-gt-loading">Loading Grand Total Amount...</span>
          ) : (
            <>
              <span className="bsbn-gt-item">
                <span className="bsbn-gt-label">Billed Amount :</span>
                <span className="bsbn-gt-value">{formatMoney(grandTotals.totalBilled)}</span>
              </span>
              <span className="bsbn-gt-item">
                <span className="bsbn-gt-label">Outstanding Amount :</span>
                <span className="bsbn-gt-value">{formatMoney(grandTotals.totalBalance)}</span>
              </span>
              <span className="bsbn-gt-item">
                <span className="bsbn-gt-label">Write Off Amount :</span>
                <span className="bsbn-gt-value">{formatMoney(grandTotals.totalWriteOff)}</span>
              </span>
              <span className="bsbn-gt-item">
                <span className="bsbn-gt-label">Paid Amount :</span>
                <span className="bsbn-gt-value">{formatMoney(grandTotals.totalPaid)}</span>
              </span>
            </>
          )}
        </div>

        {viewMode === "detailed" && (
          <div className="bsbn-detail-toolbar">
            <input
              className="bsbn-input"
              value={detailSearch}
              onChange={(e) => setDetailSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDetailSearch()}
              placeholder="Bill # / Case # / Patient"
            />
            <button type="button" className="bsbn-btn bsbn-btn-search" onClick={handleDetailSearch} disabled={detailLoading}>
              <Search size={14} />
              Search
            </button>
            <button
              type="button"
              className="bsbn-btn bsbn-btn-reset"
              onClick={() => {
                setDetailSearch("");
                loadAllDetails(1, detailPageSize, "");
              }}
              disabled={detailLoading}
            >
              <RefreshCw size={14} />
              Reset
            </button>
            <button type="button" className="bsbn-btn bsbn-btn-reset" onClick={handleDetailDownload} disabled={detailLoading || !detailRows.length}>
              <Download size={14} />
              Excel
            </button>
          </div>
        )}
      </div>

      {viewMode === "summary" && (
        <div className="bsbn-table-wrap">
          <table className="bsbn-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Company Id</th>
                <th>Bills</th>
                <th className="bsbn-num">Billed Amount</th>
                <th className="bsbn-num">OutStanding Amount</th>
                <th className="bsbn-num">WriteOff Amount</th>
                <th className="bsbn-num">Paid Amount</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={7} className="bsbn-empty">
                    {loading ? "Loading..." : "Enter bill numbers or select company, then Search"}
                  </td>
                </tr>
              ) : (
                summary.map((r) => (
                  <tr key={r.companyId}>
                    <td>
                      {r.totalCount > 0 ? (
                        <button type="button" className="bsbn-link" onClick={() => openDetails(r.companyId, r.companyName)}>
                          {r.companyName}
                        </button>
                      ) : (
                        r.companyName
                      )}
                    </td>
                    <td>{r.companyId}</td>
                    <td>
                      {r.totalCount > 0 ? (
                        <button type="button" className="bsbn-link" onClick={() => openDetails(r.companyId, r.companyName)}>
                          {r.totalCount}
                        </button>
                      ) : (
                        r.totalCount
                      )}
                    </td>
                    <td className="bsbn-num">{formatMoney(r.totalBilled)}</td>
                    <td className="bsbn-num">{formatMoney(r.totalBalance)}</td>
                    <td className="bsbn-num">{formatMoney(r.totalWriteOff)}</td>
                    <td className="bsbn-num">{formatMoney(r.totalPaid)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "detailed" && (
        <>
          <div className="bsbn-table-wrap">
            {detailLoading && <div className="bsbn-loading">Loading...</div>}
            <table className="bsbn-table">
              <thead>
                <tr>
                  <th>Attorney</th>
                  <th>Bill #</th>
                  <th>Case #</th>
                  <th>Bill Date</th>
                  <th className="bsbn-num">Billed</th>
                  <th className="bsbn-num">Paid</th>
                  <th className="bsbn-num">Write Off</th>
                  <th>Paid Flag</th>
                  <th className="bsbn-num">Amount Applied</th>
                  <th className="bsbn-num">Balance</th>
                  <th className="bsbn-num">Interest</th>
                  <th>Type</th>
                  <th>Test Type</th>
                  <th>Write Off Flag</th>
                  <th>Payment State</th>
                  <th>Status</th>
                  <th>First Visit</th>
                  <th>Proc Date</th>
                  <th>Specialty</th>
                  <th>Procedure Group</th>
                  <th>Status Code</th>
                  <th>Transfer</th>
                  <th>Denial</th>
                  <th>Verification</th>
                  <th>EOR</th>
                  <th>Provider</th>
                  <th>Case Type</th>
                  <th>Case Status</th>
                  <th>Notes</th>
                  <th>Company</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Patient</th>
                  <th>Claim #</th>
                  <th>Policy #</th>
                  <th>Has Payment</th>
                  <th>Collection</th>
                  <th>Collection Id</th>
                  <th>Insurance</th>
                  <th>Denial Reasons</th>
                  <th>Verification</th>
                  <th>DOB</th>
                  <th>SSN</th>
                  <th>Cell</th>
                  <th>Home Phone</th>
                  <th>DOA</th>
                  <th className="bsbn-address">Address</th>
                  <th>PI Attorney</th>
                  <th>Attorney Phone</th>
                  <th>Funder</th>
                  <th>Portfolio</th>
                  <th>Batch</th>
                  <th>Funded</th>
                  <th>Funder Note</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.length === 0 ? (
                  <tr>
                    <td colSpan={53} className="bsbn-empty">
                      {detailLoading ? "" : "Enter bill numbers or select company, then switch to Detailed"}
                    </td>
                  </tr>
                ) : (
                  detailRows.map((r) => (
                    <tr key={r.szBillNumber}>
                      <td>{r.attorney}</td>
                      <td>{r.szBillNumber}</td>
                      <td>{r.szCaseNo}</td>
                      <td>{formatDate(r.dtBillDate)}</td>
                      <td className="bsbn-num">{formatMoney(r.fltBillAmount)}</td>
                      <td className="bsbn-num">{formatMoney(r.paidAmount)}</td>
                      <td className="bsbn-num">{formatMoney(r.fltWriteOff)}</td>
                      <td>{r.bitPaid}</td>
                      <td className="bsbn-num">{formatMoney(r.fltAmountApplied)}</td>
                      <td className="bsbn-num">{formatMoney(r.fltBalance)}</td>
                      <td className="bsbn-num">{formatMoney(r.fltInterest)}</td>
                      <td>{r.szType}</td>
                      <td>{r.szTestType}</td>
                      <td>{r.bitWriteOffFlag}</td>
                      <td>{r.iPaymentState}</td>
                      <td>{r.szBillStatusName}</td>
                      <td>{formatDate(r.dtFirstVisitDate)}</td>
                      <td>{r.procDate}</td>
                      <td>{r.speciality}</td>
                      <td>{r.szProcedureGroup}</td>
                      <td>{r.szBillStatusCode}</td>
                      <td>{r.btTransfer ? "Yes" : "No"}</td>
                      <td>{r.btDenial}</td>
                      <td>{r.btVerification}</td>
                      <td>{r.btEor}</td>
                      <td>{r.szProviderName}</td>
                      <td>{r.mstCaseType}</td>
                      <td>{r.caseStatus}</td>
                      <NoteCell text={r.szBillNotes} onOpen={setPopup} />
                      <td>{r.companyName}</td>
                      <td>{r.szPatientFirstName}</td>
                      <td>{r.szPatientLastName}</td>
                      <td>{r.szPatientName}</td>
                      <td>{r.szClaimNumber}</td>
                      <td>{r.szPolicyNo}</td>
                      <td>{r.hasPayment}</td>
                      <td>{r.collectionName}</td>
                      <td>{r.szBillCollectionId}</td>
                      <td>{r.insuranceName}</td>
                      <td className="bsbn-wrap">{r.denialReasons}</td>
                      <td className="bsbn-wrap">{r.verification}</td>
                      <td>{formatDate(r.dob)}</td>
                      <td>{r.ssn}</td>
                      <td>{r.patientCellPhone}</td>
                      <td>{r.patientHomePhone}</td>
                      <td>{formatDate(r.doa)}</td>
                      <td className="bsbn-address">{joinAddress(r.address, r.city, r.state, r.zip)}</td>
                      <td>{r.piAttorney}</td>
                      <td>{r.attorneyPhone}</td>
                      <td>{r.funderName}</td>
                      <td>{r.portfolioName}</td>
                      <td>{r.batchNumber}</td>
                      <td>{r.isFunded}</td>
                      <NoteCell text={r.funderNote} onOpen={setPopup} />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {detailTotal > 0 && (
            <div className="bsbn-pager">
              <Select
                options={pageSizeOptions}
                value={pageSizeOptions.find((o) => o.value === detailPageSize)}
                onChange={(opt) => loadAllDetails(1, opt.value, appliedDetailSearch)}
                styles={selectStyles}
                menuPlacement="top"
                isSearchable={false}
              />
              <span>
                {(safeDetailPage - 1) * detailPageSize + 1}-{Math.min(safeDetailPage * detailPageSize, detailTotal)} of {detailTotal}
              </span>
              <button type="button" className="bsbn-btn bsbn-btn-reset" disabled={safeDetailPage <= 1 || detailLoading} onClick={() => loadAllDetails(safeDetailPage - 1, detailPageSize, appliedDetailSearch)}>
                Prev
              </button>
              <button type="button" className="bsbn-btn bsbn-btn-reset" disabled={safeDetailPage >= detailTotalPages || detailLoading} onClick={() => loadAllDetails(safeDetailPage + 1, detailPageSize, appliedDetailSearch)}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      {popup && (
        <div className="bsbn-overlay" onClick={() => setPopup(null)}>
          <div className="bsbn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bsbn-modal-header">
              <h3>{popup.title}</h3>
              <button type="button" className="bsbn-close" onClick={() => setPopup(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="bsbn-modal-body">
              <pre>{popup.text}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillSearchByNumbersReport;